import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Check, MapPin, ArrowRight, ArrowDown, Flame, Play, Phone, Mail } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import SmartMedia from "@/components/site/SmartMedia";
import Lightbox from "@/components/site/Lightbox";
import { hasMedia } from "@/lib/media";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import BudgetCalculator, { prettyDate } from "@/components/site/BudgetCalculator";
import HoldDatesDialog from "@/components/site/HoldDatesDialog";
import AlbumBook from "@/components/site/AlbumBook";
import DecorCalculator from "@/components/site/DecorCalculator";
import api from "@/lib/api";
import { useSettings } from "@/lib/settings";

const Paras = ({ text, className = "" }) => (
  <div className={`para-stack ${className}`}>
    {String(text || "").split(/\n\s*\n/).filter(Boolean).map((t, i) => (
      <p key={i} className="body-lead">{t}</p>
    ))}
  </div>
);

const watchEmbed = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?rel=0`;
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
};

// Section vertical padding (new global tighter default = "normal"). Fixed strings
// so Tailwind's JIT can see them.
const SECTION_PAD = {
  none: "py-0",
  compact: "py-4 md:py-6",
  normal: "py-8 md:py-12",
  spacious: "py-16 md:py-24",
};
const sectionPad = (spacing) => SECTION_PAD[spacing] || SECTION_PAD.normal;

const alignCls = (a) => (a === "center" ? "text-center" : a === "right" ? "text-right" : "");
const alignMx = (a) => (a === "center" ? "mx-auto" : a === "right" ? "ml-auto" : "");

function TextSection({ d }) {
  const a = d.align;
  return (
    <section className={`${d.dark ? "bg-ink text-ivory" : ""} ${sectionPad(d.spacing)}`}>
      <div className={`container-x ${alignCls(a)}`}>
        {d.overline && <Reveal><div className={`overline ${d.dark ? "text-ivory/60" : "accent-color"}`}>{d.overline}</div></Reveal>}
        {d.heading && <Reveal delay={100}><h2 className={`display-2 mt-6 ${alignMx(a)} max-w-3xl ${d.dark ? "text-ivory" : ""}`}>{d.heading}</h2></Reveal>}
        {d.body && <Reveal delay={200}><Paras text={d.body} className={`mt-8 max-w-2xl ${alignMx(a)} ${d.dark ? "text-ivory/80" : ""}`} /></Reveal>}
      </div>
    </section>
  );
}

function TextImageSection({ d }) {
  const imgLeft = d.side === "left";
  return (
    <section className={`container-x ${sectionPad(d.spacing)}`}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <Reveal className={`lg:col-span-6 ${imgLeft ? "lg:order-1" : "lg:order-2"}`}>
          {hasMedia(d.media) && (
            <div className="aspect-[4/5] overflow-hidden zoom-on-hover relative">
              <SmartMedia media={d.media} alt={d.heading || "Section image"} className="w-full h-full object-cover" />
            </div>
          )}
        </Reveal>
        <Reveal delay={150} className={`lg:col-span-5 ${imgLeft ? "lg:order-2 lg:col-start-8" : "lg:order-1"} ${alignCls(d.align)}`}>
          {d.overline && <div className="overline accent-color">{d.overline}</div>}
          {d.heading && <h2 className="display-2 mt-6">{d.heading}</h2>}
          {d.body && <Paras text={d.body} className="mt-6" />}
        </Reveal>
      </div>
    </section>
  );
}

function CardsSection({ d, enquiry, slug }) {
  const items = (d.items || []).filter((c) => c.title || hasMedia(c.media));
  if (!items.length) return null;
  const cols = { "2": "lg:grid-cols-2", "3": "lg:grid-cols-3", "4": "lg:grid-cols-4" }[String(d.columns || "3")];
  const cardCta = (c) => {
    if (c.cta_url) window.location.href = c.cta_url;
    else enquiry.open(slug);
  };
  return (
    <section className={`container-x ${sectionPad(d.spacing)}`}>
      <div className={alignCls(d.align)}>
        {d.overline && <Reveal><div className="overline accent-color">{d.overline}</div></Reveal>}
        {d.heading && <Reveal delay={100}><h2 className="display-2 mt-6">{d.heading}</h2></Reveal>}
      </div>
      <div className={`mt-12 grid grid-cols-1 md:grid-cols-2 ${cols} gap-8 md:gap-10`}>
        {items.map((c, i) => (
          <Reveal key={i} delay={i * 80}>
            <div className="group">
              {hasMedia(c.media) && (
                <div className="aspect-[4/5] zoom-on-hover overflow-hidden relative">
                  <SmartMedia media={c.media} alt={c.title || "Card"} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="mt-5 flex items-start justify-between gap-4">
                <div className="font-display text-2xl">{c.title}</div>
                {c.meta && <div className="text-xs accent-color whitespace-nowrap pt-1">{c.meta}</div>}
              </div>
              {c.description && <div className="mt-2 text-sm text-graphite font-light leading-relaxed">{c.description}</div>}
              {c.cta_text && (
                <button onClick={() => cardCta(c)} className="link-arrow mt-4" data-testid={`card-cta-${i}`}>
                  {c.cta_text} <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function BannerSection({ d }) {
  if (!hasMedia(d.media)) return null;
  return (
    <section className={sectionPad(d.spacing)}>
      <Reveal>
        <div className={`relative w-full overflow-hidden ${d.height === "tall" ? "h-[85svh]" : "h-[55svh]"}`}>
          <SmartMedia media={d.media} alt={d.caption || "Banner"} className="absolute inset-0 w-full h-full object-cover" />
          {d.caption && (
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-ink/70 to-transparent pt-24 pb-8">
              <div className="container-x text-ivory font-display text-2xl md:text-4xl">{d.caption}</div>
            </div>
          )}
        </div>
      </Reveal>
    </section>
  );
}

function GalleryGridSection({ d }) {
  const items = (d.items || []).filter(hasMedia);
  const [lbIndex, setLbIndex] = useState(null);
  if (!items.length) return null;
  const cols = { "2": "md:grid-cols-2", "3": "md:grid-cols-3", "4": "md:grid-cols-4" }[String(d.columns || "3")];
  return (
    <section className={`container-x ${sectionPad(d.spacing)}`}>
      <div className={alignCls(d.align)}>
        {d.overline && <Reveal><div className="overline accent-color">{d.overline}</div></Reveal>}
        {d.heading && <Reveal delay={100}><h2 className="display-2 mt-6">{d.heading}</h2></Reveal>}
      </div>
      <div className={`mt-12 grid grid-cols-2 ${cols} gap-3 md:gap-5`}>
        {items.map((m, i) => (
          <Reveal key={i} delay={i * 50} className="aspect-[3/4]">
            <button type="button" onClick={() => setLbIndex(i)} className="w-full h-full zoom-on-hover relative cursor-zoom-in block text-left" aria-label={`View photo ${i + 1}`} data-testid={`gallery-grid-item-${i}`}>
              <SmartMedia media={m} alt={`${d.heading || "Gallery"} ${i + 1}`} className="w-full h-full object-cover" />
            </button>
          </Reveal>
        ))}
      </div>
      <Lightbox items={items} index={lbIndex} setIndex={setLbIndex} altPrefix={d.heading || "Gallery"} />
    </section>
  );
}

function VideoEmbedSection({ d }) {
  if (!d.url) return null;
  const embed = watchEmbed(d.url);
  const isFile = /\.(mp4|webm|mov)(\?|$)/i.test(d.url);
  return (
    <section className={`container-x ${sectionPad(d.spacing)}`}>
      <div className={alignCls(d.align)}>
        {d.overline && <Reveal><div className="overline accent-color">{d.overline}</div></Reveal>}
        {d.heading && <Reveal delay={100}><h2 className={`display-2 mt-6 max-w-2xl ${alignMx(d.align)}`}>{d.heading}</h2></Reveal>}
      </div>
      <Reveal delay={200} className="mt-12">
        <div className="aspect-video w-full bg-ink/5">
          {embed ? (
            <iframe src={embed} title={d.heading || "Video"} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          ) : isFile ? (
            <video src={d.url} controls playsInline className="w-full h-full object-cover" />
          ) : (
            <iframe src={d.url} title={d.heading || "Video"} className="w-full h-full" allowFullScreen />
          )}
        </div>
      </Reveal>
    </section>
  );
}

function QuoteSection({ d }) {
  if (!d.text) return null;
  return (
    <section className={`bg-ivory ${sectionPad(d.spacing)}`}>
      <div className={`container-x max-w-4xl mx-auto ${alignCls(d.align) || "text-center"}`}>
        <Reveal><div className="font-display text-6xl accent-color leading-none">&ldquo;</div></Reveal>
        <Reveal delay={100}><p className="font-display text-3xl md:text-5xl leading-[1.15] tracking-tight mt-2">{d.text}</p></Reveal>
        {d.attribution && <Reveal delay={200}><div className="overline accent-color mt-10">{d.attribution}</div></Reveal>}
      </div>
    </section>
  );
}

function CTASection({ d, enquiry, slug }) {
  const dark = d.style !== "light";
  const a = d.align || "center";
  const onClick = () => {
    if (d.button_action === "call" && d.button_phone) { window.location.href = `tel:${String(d.button_phone).replace(/[^\d+]/g, "")}`; return; }
    if (d.button_action === "link" && d.button_url) window.location.href = d.button_url;
    else enquiry.open(slug);
  };
  return (
    <section className={`${dark ? "bg-ink text-ivory" : "bg-ivory"} ${sectionPad(d.spacing)}`}>
      <div className={`container-x ${alignCls(a) || "text-left"}`}>
        {d.overline && <Reveal><div className={`overline ${dark ? "text-ivory/60" : "accent-color"}`}>{d.overline}</div></Reveal>}
        {d.heading && <Reveal delay={100}><h2 className={`display-2 mt-6 max-w-3xl ${alignMx(a)} ${dark ? "text-ivory" : ""}`}>{d.heading}</h2></Reveal>}
        {d.body && <Reveal delay={150}><p className={`body-lead mt-6 max-w-xl ${alignMx(a)} ${dark ? "text-ivory/80" : ""}`}>{d.body}</p></Reveal>}
        <Reveal delay={250}>
          <button onClick={onClick} data-testid="custom-cta-button" className={`mt-10 px-10 py-4 text-xs uppercase tracking-[0.22em] transition-colors ${dark ? "bg-ivory text-ink hover:bg-[var(--prop-accent)] hover:text-white" : "bg-ink text-ivory hover:bg-[var(--prop-accent)]"}`}>
            {d.button_text || "Enquire now"}
          </button>
        </Reveal>
      </div>
    </section>
  );
}

function StatsSection({ d }) {
  const items = (d.items || []).filter((i) => i.value || i.label);
  if (!items.length) return null;
  return (
    <section className={`container-x ${sectionPad(d.spacing)}`}>
      <div className={alignCls(d.align)}>
        {d.overline && <Reveal><div className="overline accent-color">{d.overline}</div></Reveal>}
        {d.heading && <Reveal delay={100}><h2 className={`display-2 mt-6 max-w-3xl ${alignMx(d.align)}`}>{d.heading}</h2></Reveal>}
      </div>
      <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-ink/10">
        {items.map((s, i) => (
          <Reveal key={i} delay={i * 80}>
            <div className="p-8 md:p-10 bg-[var(--prop-surface,#fff)] h-full surface-bg">
              <div className="font-display text-4xl md:text-6xl accent-color">{s.value}</div>
              <div className="overline mt-4">{s.label}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ChecklistSection({ d }) {
  const items = (d.items || []).filter(Boolean);
  if (!items.length) return null;
  const cols = String(d.columns || "2") === "3" ? "md:grid-cols-3" : "md:grid-cols-2";
  return (
    <section className={`bg-ivory ${sectionPad(d.spacing)}`}>
      <div className="container-x">
        <div className={alignCls(d.align)}>
          {d.overline && <Reveal><div className="overline accent-color">{d.overline}</div></Reveal>}
          {d.heading && <Reveal delay={100}><h2 className={`display-2 mt-6 max-w-3xl ${alignMx(d.align)}`}>{d.heading}</h2></Reveal>}
        </div>
        <div className={`mt-12 grid grid-cols-1 ${cols} gap-x-12 gap-y-5`}>
          {items.map((it, i) => (
            <Reveal key={i} delay={i * 40}>
              <div className="flex items-start gap-4 border-b border-ink/10 pb-5">
                <Check className="w-4 h-4 accent-color mt-1 shrink-0" />
                <span className="text-sm md:text-base font-light">{it}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqExtraSection({ d }) {
  const items = (d.items || []).filter((f) => f.q);
  if (!items.length) return null;
  return (
    <section className={`container-x ${sectionPad(d.spacing)}`}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className={`lg:col-span-4 ${alignCls(d.align)}`}>
          {d.overline && <Reveal><div className="overline accent-color">{d.overline}</div></Reveal>}
          {d.heading && <Reveal delay={100}><h2 className="display-2 mt-6">{d.heading}</h2></Reveal>}
        </div>
        <div className="lg:col-span-8">
          <Accordion type="single" collapsible className="border-t border-ink/10">
            {items.map((f, i) => (
              <AccordionItem key={i} value={`cf${i}`} className="border-b border-ink/10">
                <AccordionTrigger className="text-left py-6 font-display text-xl md:text-2xl hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="pb-6 text-sm text-graphite font-light leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ContactSection({ d }) {
  const { settings } = useSettings();
  const phone = (d.phone || settings?.contact?.phone || "").trim();
  const email = (d.email || settings?.contact?.email || "").trim();
  const hasDetails = d.address || phone || email;
  if (!d.heading && !d.overline && !hasDetails && !d.map_query) return null;
  return (
    <section className={`container-x ${sectionPad(d.spacing)}`} data-testid="contact-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className={d.map_query ? "lg:col-span-5" : "lg:col-span-12 max-w-2xl"}>
          {d.overline && <Reveal><div className="overline accent-color">{d.overline}</div></Reveal>}
          {d.heading && <Reveal delay={100}><h2 className="display-2 mt-6">{d.heading}</h2></Reveal>}
          <Reveal delay={200}>
            <div className="mt-10 space-y-6">
              {d.address && (
                <div className="flex items-start gap-4" data-testid="contact-address">
                  <MapPin className="w-5 h-5 accent-color shrink-0 mt-1" />
                  <div className="text-sm text-graphite font-light leading-relaxed whitespace-pre-line">{d.address}</div>
                </div>
              )}
              {phone && (
                <a href={`tel:${phone.replace(/[^\d+]/g, "")}`} className="flex items-center gap-4 group" data-testid="contact-phone">
                  <Phone className="w-5 h-5 accent-color shrink-0" />
                  <span className="text-sm text-graphite font-light group-hover:text-ink transition-colors">{phone}</span>
                </a>
              )}
              {email && (
                <a href={`mailto:${email}`} className="flex items-center gap-4 group" data-testid="contact-email">
                  <Mail className="w-5 h-5 accent-color shrink-0" />
                  <span className="text-sm text-graphite font-light group-hover:text-ink transition-colors">{email}</span>
                </a>
              )}
              {d.map_query && (
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(d.map_query)}`} target="_blank" rel="noopener noreferrer" className="link-arrow inline-flex items-center gap-2" data-testid="contact-directions">
                  Get directions <ArrowRight className="w-4 h-4" />
                </a>
              )}
            </div>
          </Reveal>
        </div>
        {d.map_query && (
          <Reveal delay={150} className="lg:col-span-7">
            <div className="aspect-[16/12] w-full" data-testid="contact-map">
              <iframe title="Location map" className="w-full h-full" loading="lazy" src={`https://www.google.com/maps?q=${encodeURIComponent(d.map_query)}&output=embed`} />
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}

function MapSection({ d }) {
  if (!d.query) return null;
  return (
    <section className={`container-x ${sectionPad(d.spacing)}`}>
      {d.overline && <Reveal><div className="overline accent-color">{d.overline}</div></Reveal>}
      {d.heading && <Reveal delay={100}><h2 className="display-2 mt-6 flex items-center gap-4"><MapPin className="w-8 h-8 accent-color" /> {d.heading}</h2></Reveal>}
      <Reveal delay={200} className="mt-10">
        <div className="aspect-[16/8] w-full">
          <iframe title="Custom map" className="w-full h-full" loading="lazy" src={`https://www.google.com/maps?q=${encodeURIComponent(d.query)}&output=embed`} />
        </div>
      </Reveal>
    </section>
  );
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export function AvailabilityCalendar({ booked = [], holds = {}, selectable = false, range = {}, onRangeChange }) {
  const [cur, setCur] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [warn, setWarn] = useState(false);
  const bookedSet = useMemo(() => new Set(booked), [booked]);
  const y = cur.getFullYear(), m = cur.getMonth();
  const firstDow = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const fmt = (d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const { start, end } = range || {};
  const fmtShort = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" });

  const hasBookedBetween = (s, e) => {
    const d = new Date(`${s}T00:00:00`);
    const stop = new Date(`${e}T00:00:00`);
    while (d <= stop) {
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (bookedSet.has(iso)) return true;
      d.setDate(d.getDate() + 1);
    }
    return false;
  };

  const pick = (iso) => {
    if (!selectable || !onRangeChange) return;
    setWarn(false);
    if (!start || (start && end)) { onRangeChange({ start: iso, end: null }); return; }
    if (iso === start) { onRangeChange({ start: iso, end: iso }); return; }
    const [s, e] = iso < start ? [iso, start] : [start, iso];
    if (hasBookedBetween(s, e)) { setWarn(true); onRangeChange({ start: iso, end: null }); return; }
    onRangeChange({ start: s, end: e });
  };

  const clear = () => { setWarn(false); onRangeChange?.({ start: null, end: null }); };
  const nDays = start && end ? Math.round((new Date(`${end}T00:00:00`) - new Date(`${start}T00:00:00`)) / 86400000) + 1 : 0;

  return (
    <div className="max-w-xl" data-testid="availability-calendar">
      <div className="flex items-center justify-between border-b border-ink/10 pb-4">
        <div className="font-display text-2xl md:text-3xl">{MONTHS[m]} <span className="text-graphite">{y}</span></div>
        <div className="flex gap-2">
          <button onClick={() => setCur(new Date(y, m - 1, 1))} aria-label="Previous month" data-testid="cal-prev" className="w-9 h-9 border border-ink/15 flex items-center justify-center hover:bg-ink hover:text-ivory transition-colors"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => setCur(new Date(y, m + 1, 1))} aria-label="Next month" data-testid="cal-next" className="w-9 h-9 border border-ink/15 flex items-center justify-center hover:bg-ink hover:text-ivory transition-colors"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      {selectable && (
        <div className="mt-4 flex items-center justify-between gap-3 min-h-[20px]" data-testid="cal-hint">
          {warn ? (
            <span className="text-xs text-red-700">That range includes a booked date — selection restarted.</span>
          ) : !start ? (
            <span className="text-xs uppercase tracking-[0.16em] text-graphite"><b className="text-ink">Step 1</b> · Tap your check-in date</span>
          ) : !end ? (
            <span className="text-xs uppercase tracking-[0.16em] font-medium" style={{ color: "var(--prop-accent)" }}>Step 1 · Now tap your check-out date</span>
          ) : (
            <span className="text-sm font-medium" data-testid="cal-selection-summary"><Check className="w-4 h-4 inline -mt-0.5 mr-1.5" style={{ color: "var(--prop-accent)" }} />{fmtShort(start)} → {fmtShort(end)} · {nDays} day{nDays === 1 ? "" : "s"}</span>
          )}
          {(start || end) && (
            <button onClick={clear} className="text-xs uppercase tracking-[0.14em] text-graphite underline underline-offset-4 hover:text-ink" data-testid="cal-clear-btn">Clear</button>
          )}
        </div>
      )}
      <div className={`grid grid-cols-7 ${selectable ? "mt-4" : "mt-6"} text-center`}>
        {DOW.map((d) => <div key={d} className="overline pb-3">{d}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const iso = fmt(d);
          const isBooked = bookedSet.has(iso);
          const isPast = new Date(y, m, d) < today;
          const free = !isPast && !isBooked;
          const heldCount = free ? holds[iso] || 0 : 0;
          const isEdge = selectable && free && (iso === start || iso === end);
          const inMid = selectable && start && end && iso > start && iso < end && free;
          let cls = "w-full h-full flex flex-col items-center justify-center text-sm transition-all duration-200 rounded-sm ";
          let style;
          if (isPast) cls += "text-ink/25";
          else if (isBooked) cls += "cal-booked text-ink/40";
          else if (isEdge) { cls += "text-white font-medium shadow-sm"; style = { background: "var(--prop-accent)" }; }
          else if (inMid) { cls += "text-ink font-medium"; style = { background: "color-mix(in srgb, var(--prop-accent) 16%, transparent)" }; }
          else cls += `font-medium ${selectable ? "cursor-pointer hover:bg-ink/[0.06]" : ""}`;
          return (
            <div key={iso} className="aspect-square p-[3px]">
              <button
                type="button"
                disabled={!free || !selectable}
                onClick={() => pick(iso)}
                className={cls}
                style={style}
                title={isBooked ? "Booked" : heldCount ? `${heldCount} guest${heldCount === 1 ? " is" : "s are"} holding this date` : undefined}
                data-testid={`cal-day-${iso}`}
              >
                {d}
                {free && !isEdge && !inMid && (heldCount ? (
                  <span className="hold-pulse flex items-center gap-[2px] mt-0.5" style={{ color: "#C2410C" }} data-testid={`cal-hold-${iso}`}>
                    <Flame className="w-2.5 h-2.5 fill-current" />
                    <span className="text-[9px] font-bold leading-none">{heldCount}</span>
                  </span>
                ) : (
                  <span className="w-1 h-1 rounded-full mt-1" style={{ background: "var(--prop-accent)" }} />
                ))}
                {isBooked && !isPast && <span className="text-[8px] uppercase tracking-[0.14em] mt-0.5 leading-none opacity-80">Booked</span>}
              </button>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-7 gap-y-2 mt-6 pt-4 border-t border-ink/10">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em]"><span className="w-2 h-2 rounded-full" style={{ background: "var(--prop-accent)" }} /> Available</div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-graphite"><span className="cal-booked inline-block w-4 h-4 rounded-sm border border-ink/10" /> Booked</div>
        {Object.keys(holds).length > 0 && (
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.18em]" style={{ color: "#C2410C" }} data-testid="cal-legend-demand"><Flame className="w-3.5 h-3.5 fill-current" /> In demand</div>
        )}
        {selectable && <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-graphite"><span className="inline-block w-4 h-4 rounded-sm" style={{ background: "var(--prop-accent)" }} /> Selected</div>}
      </div>
      {Object.keys(holds).length > 0 && (
        <p className="text-xs text-graphite mt-3 leading-relaxed" data-testid="cal-fcfs-note">
          Dates with a <Flame className="w-3 h-3 inline -mt-0.5 fill-current" style={{ color: "#C2410C" }} /> flame are being held by other guests right now. Holds last 48 hours and bookings are accepted <b className="text-ink">first come, first served</b>.
        </p>
      )}
    </div>
  );
}

function CalendarSection({ d, enquiry, slug }) {
  const [range, setRange] = useState({ start: null, end: null });
  const [holds, setHolds] = useState({});
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdEstimate, setHoldEstimate] = useState("");
  const calcRef = useRef(null);
  const calcCfg = d.calc || {};
  const complete = !!(range.start && range.end);
  const days = complete ? Math.round((new Date(`${range.end}T00:00:00`) - new Date(`${range.start}T00:00:00`)) / 86400000) + 1 : 0;
  const showCalc = !!calcCfg.enabled && complete;

  const refreshHolds = () => {
    if (!slug) return;
    api.get(`/public/date-holds/${slug}`).then((r) => setHolds(r.data.counts || {})).catch(() => {});
  };
  useEffect(refreshHolds, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const rangeHoldMax = useMemo(() => {
    if (!complete) return 0;
    let max = 0;
    const cur = new Date(`${range.start}T00:00:00`);
    const stop = new Date(`${range.end}T00:00:00`);
    while (cur <= stop) {
      const iso = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(cur.getDate()).padStart(2, "0")}`;
      max = Math.max(max, holds[iso] || 0);
      cur.setDate(cur.getDate() + 1);
    }
    return max;
  }, [complete, range, holds]);

  const enquireDates = () => {
    const pre = complete
      ? { preferred_date: range.start, message: `Interested in ${prettyDate(range.start)} → ${prettyDate(range.end)} (${days} day${days === 1 ? "" : "s"}).` }
      : undefined;
    enquiry.open(slug, pre);
  };

  const scrollToCalc = () => calcRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <section className={`bg-ivory ${sectionPad(d.spacing)}`} data-testid="section-calendar">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className={`lg:col-span-5 ${alignCls(d.align)}`}>
          {d.overline && <Reveal><div className="overline accent-color">{d.overline}</div></Reveal>}
          {d.heading && <Reveal delay={100}><h2 className="display-2 mt-6">{d.heading}</h2></Reveal>}
          {d.note && <Reveal delay={150}><p className="body-lead mt-6">{d.note}</p></Reveal>}
          <Reveal delay={200}>
            <p className="text-sm text-graphite mt-6 leading-relaxed">
              Pick your check-in and check-out dates on the calendar{calcCfg.enabled ? " — a personalised budget estimate opens the moment you do" : ""}.
            </p>
            <button onClick={enquireDates} className="btn-primary mt-8" data-testid="calendar-enquire-btn">
              {complete ? "Enquire for these dates" : "Check these dates"}
            </button>
          </Reveal>
        </div>
        <Reveal delay={150} className="lg:col-span-7">
          <AvailabilityCalendar booked={d.booked || []} holds={holds} selectable range={range} onRangeChange={setRange} />
          {complete && (
            <div className="mt-6 calc-enter">
              <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                {showCalc && (
                  <button
                    type="button"
                    onClick={scrollToCalc}
                    className="inline-flex items-center justify-center gap-3 px-8 py-4 text-xs uppercase tracking-[0.22em] font-medium text-white transition-all hover:-translate-y-0.5 hover:shadow-lg"
                    style={{ background: "var(--prop-accent)" }}
                    data-testid="calendar-continue-btn"
                  >
                    Step 2 · Continue to your estimate <ArrowDown className="w-4 h-4 animate-bounce" />
                  </button>
                )}
                {!showCalc && (
                  <button
                    type="button"
                    onClick={() => { setHoldEstimate(""); setHoldOpen(true); }}
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 text-xs uppercase tracking-[0.22em] font-medium border transition-all hover:-translate-y-0.5"
                    style={{ borderColor: "#C2410C", color: "#C2410C" }}
                    data-testid="calendar-hold-btn"
                  >
                    <Flame className="w-4 h-4 fill-current" /> Hold these dates · free for 48 hrs
                  </button>
                )}
              </div>
              {rangeHoldMax > 0 && (
                <p className="text-sm mt-4 leading-relaxed" style={{ color: "#C2410C" }} data-testid="calendar-urgency-note">
                  <Flame className="w-4 h-4 inline -mt-1 mr-1 fill-current hold-pulse" />
                  <b>{rangeHoldMax} other guest{rangeHoldMax === 1 ? " is" : "s are"}</b> holding dates in your range right now — bookings are accepted first come, first served.
                </p>
              )}
            </div>
          )}
        </Reveal>
      </div>
      {showCalc && (
        <div ref={calcRef} className="container-x mt-12 md:mt-16 scroll-mt-24">
          <BudgetCalculator cfg={calcCfg} range={{ ...range, days }} enquiry={enquiry} slug={slug} onHold={(est) => { setHoldEstimate(est || ""); setHoldOpen(true); }} />
        </div>
      )}
      <HoldDatesDialog
        open={holdOpen}
        onClose={() => setHoldOpen(false)}
        slug={slug}
        range={range}
        days={days}
        estimate={holdEstimate}
        onSuccess={refreshHolds}
      />
    </section>
  );
}

function EmbedSection({ d }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    ref.current.innerHTML = d.code || "";
    ref.current.querySelectorAll("script").forEach((old) => {
      const s = document.createElement("script");
      [...old.attributes].forEach((a) => s.setAttribute(a.name, a.value));
      s.text = old.text || "";
      old.replaceWith(s);
    });
  }, [d.code]);
  if (!d.code) return null;
  return (
    <section className={`container-x ${sectionPad(d.spacing)}`}>
      <div className={alignCls(d.align)}>
        {d.overline && <Reveal><div className="overline accent-color">{d.overline}</div></Reveal>}
        {d.heading && <Reveal delay={100}><h2 className={`display-2 mt-6 max-w-3xl ${alignMx(d.align)}`}>{d.heading}</h2></Reveal>}
      </div>
      <div ref={ref} className="mt-10" />
    </section>
  );
}

function DividerSection({ d }) {
  if (d.style === "space-sm") return <div className="h-16 md:h-24" />;
  if (d.style === "space-lg") return <div className="h-32 md:h-48" />;
  return <div className="container-x py-8"><div className="hairline" /></div>;
}

function CelebrationsSection({ d }) {
  const items = (d.items || []).filter((it) => it && (it.title || it.icon || it.description));
  if (!items.length && !d.heading && !d.overline) return null;
  const cols = d.columns || "3";
  const style = d.style || "cards";
  const colClass = cols === "2" ? "md:grid-cols-2" : cols === "4" ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-2 lg:grid-cols-3";

  return (
    <section className={sectionPad(d.spacing)} data-testid="section-celebrations">
      <div className="container-x">
        {(d.overline || d.heading || d.intro) && (
          <div className={`max-w-3xl mb-12 md:mb-16 ${alignCls(d.align)} ${alignMx(d.align)}`}>
            {d.overline && <Reveal><div className="overline accent-color">{d.overline}</div></Reveal>}
            {d.heading && <Reveal delay={100}><h2 className="display-2 mt-5">{d.heading}</h2></Reveal>}
            {d.intro && <Reveal delay={200}><p className="body-lead mt-6 text-ink/70">{d.intro}</p></Reveal>}
          </div>
        )}

        {style === "chips" && (
          <Reveal delay={150}>
            <div className="flex flex-wrap gap-3 md:gap-4">
              {items.map((it, i) => (
                <div
                  key={i}
                  className="group inline-flex items-center gap-3 pl-4 pr-6 py-3 bg-bone/60 border border-ink/10 rounded-full transition-all duration-300 hover:border-clay hover:bg-white hover:-translate-y-0.5 hover:shadow-sm"
                  data-testid={`celebration-chip-${i}`}
                >
                  {it.icon && <span className="text-xl leading-none">{it.icon}</span>}
                  <span className="text-sm md:text-[15px] font-medium text-ink group-hover:text-clay transition-colors">{it.title}</span>
                </div>
              ))}
            </div>
          </Reveal>
        )}

        {style === "list" && (
          <div className="max-w-3xl divide-y divide-ink/10 border-t border-b border-ink/10">
            {items.map((it, i) => (
              <Reveal key={i} delay={i * 60}>
                <div className="flex items-start gap-6 py-6 group" data-testid={`celebration-list-${i}`}>
                  {it.icon && (
                    <div className="w-14 h-14 flex-shrink-0 rounded-full bg-bone flex items-center justify-center text-2xl transition-all duration-300 group-hover:bg-clay group-hover:-translate-y-0.5">
                      <span>{it.icon}</span>
                    </div>
                  )}
                  <div className="pt-1">
                    <h3 className="font-display text-2xl leading-tight group-hover:text-clay transition-colors">{it.title}</h3>
                    {it.description && <p className="text-sm md:text-[15px] text-ink/65 mt-2 leading-relaxed">{it.description}</p>}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}

        {style === "cards" && (
          <div className={`grid grid-cols-1 ${colClass} gap-4 md:gap-6`}>
            {items.map((it, i) => (
              <Reveal key={i} delay={i * 50}>
                <div
                  className="group h-full p-7 md:p-9 bg-white border border-ink/10 rounded-md transition-all duration-500 hover:border-clay hover:-translate-y-1 hover:shadow-[0_20px_50px_-30px_rgba(0,0,0,0.35)]"
                  data-testid={`celebration-card-${i}`}
                >
                  {it.icon && (
                    <div className="text-4xl md:text-5xl leading-none mb-5 transition-transform duration-500 group-hover:scale-110 origin-left">
                      <span>{it.icon}</span>
                    </div>
                  )}
                  <h3 className="font-display text-xl md:text-2xl leading-tight group-hover:text-clay transition-colors">{it.title}</h3>
                  {it.description && <p className="text-sm md:text-[15px] text-ink/65 mt-3 leading-relaxed">{it.description}</p>}
                  <div className="mt-6 w-10 h-px bg-ink/20 group-hover:w-16 group-hover:bg-clay transition-all duration-500" />
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

const RENDERERS = {
  text: TextSection,
  text_image: TextImageSection,
  cards: CardsSection,
  banner: BannerSection,
  gallery_grid: GalleryGridSection,
  video_embed: VideoEmbedSection,
  quote: QuoteSection,
  cta: CTASection,
  stats: StatsSection,
  checklist: ChecklistSection,
  faq_extra: FaqExtraSection,
  map: MapSection,
  contact: ContactSection,
  calendar: CalendarSection,
  album: AlbumSection,
  team: TeamSection,
  video_strip: VideoStripSection,
  decor_calc: DecorCalcSection,
  embed: EmbedSection,
  divider: DividerSection,
  celebrations: CelebrationsSection,
};

const ytId = (url) => {
  const m = String(url || "").match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/|live\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? m[1] : null;
};

function VideoStripCard({ item, index }) {
  const [playing, setPlaying] = useState(false);
  // Duck the ambient music while this video plays with sound
  useEffect(() => {
    if (!playing) return;
    window.dispatchEvent(new Event("hr-media-play"));
    return () => window.dispatchEvent(new Event("hr-media-stop"));
  }, [playing]);
  const id = ytId(item.url);
  if (!id) return null;
  return (
    <div className="flex-shrink-0 w-[280px] md:w-[400px] snap-start" data-testid={`video-strip-item-${index}`}>
      <div className="relative aspect-video overflow-hidden bg-ink group">
        {playing ? (
          <iframe
            src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0&playsinline=1`}
            title={item.title || "Video"}
            className="absolute inset-0 w-full h-full border-0"
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="absolute inset-0 w-full h-full text-left"
            aria-label={`Play ${item.title || "video"}`}
            data-testid={`video-strip-play-${index}`}
          >
            <img
              src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
              alt={item.title || "Video thumbnail"}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              loading="lazy"
            />
            <span className="absolute inset-0 bg-ink/25 transition-colors duration-300 group-hover:bg-ink/10" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-14 h-14 rounded-full bg-ivory/95 flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110">
                <Play className="w-5 h-5 text-ink fill-current ml-0.5" />
              </span>
            </span>
          </button>
        )}
      </div>
      {item.title && <div className="pt-3 text-sm font-medium text-ink leading-snug">{item.title}</div>}
    </div>
  );
}

function VideoStripSection({ d }) {
  const items = (d.items || []).filter((it) => it && ytId(it.url));
  const stripRef = useRef(null);
  const scrollBy = (dir) => stripRef.current?.scrollBy({ left: dir * (stripRef.current.clientWidth * 0.75), behavior: "smooth" });
  if (!items.length && !d.heading && !d.overline) return null;
  return (
    <section className={sectionPad(d.spacing)} data-testid="section-video-strip">
      <div className="container-x">
        {(d.overline || d.heading || d.intro) && (
          <div className="flex items-end justify-between gap-6 mb-10 md:mb-12">
            <div className={`max-w-3xl ${alignCls(d.align)} ${d.align === "center" ? "mx-auto flex-1" : d.align === "right" ? "ml-auto" : ""}`}>
              {d.overline && <Reveal><div className="overline accent-color">{d.overline}</div></Reveal>}
              {d.heading && <Reveal delay={100}><h2 className="display-2 mt-5">{d.heading}</h2></Reveal>}
              {d.intro && <Reveal delay={200}><p className={`body-lead mt-6 text-ink/70 ${alignMx(d.align)}`}>{d.intro}</p></Reveal>}
            </div>
            {items.length > 2 && (
              <div className="hidden md:flex gap-2 pb-1">
                <button type="button" onClick={() => scrollBy(-1)} aria-label="Scroll videos left" className="w-11 h-11 rounded-full border border-ink/15 flex items-center justify-center hover:bg-ink hover:text-ivory transition-colors" data-testid="video-strip-prev">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button type="button" onClick={() => scrollBy(1)} aria-label="Scroll videos right" className="w-11 h-11 rounded-full border border-ink/15 flex items-center justify-center hover:bg-ink hover:text-ivory transition-colors" data-testid="video-strip-next">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        )}
        <Reveal delay={150}>
          <div ref={stripRef} className="flex gap-5 md:gap-7 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2 -mx-6 px-6 md:mx-0 md:px-0" data-testid="video-strip">
            {items.map((it, i) => <VideoStripCard key={i} item={it} index={i} />)}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function DecorCalcSection({ d, slug }) {
  return (
    <section className={`bg-bone/40 ${sectionPad(d.spacing)}`} data-testid="section-decor-calc">
      <div className="container-x">
        {(d.overline || d.heading || d.intro) && (
          <div className={`max-w-3xl mb-10 md:mb-14 ${alignCls(d.align)} ${alignMx(d.align)}`}>
            {d.overline && <Reveal><div className="overline accent-color">{d.overline}</div></Reveal>}
            {d.heading && <Reveal delay={100}><h2 className="display-2 mt-5">{d.heading}</h2></Reveal>}
            {d.intro && <Reveal delay={200}><p className="body-lead mt-6 text-ink/70">{d.intro}</p></Reveal>}
          </div>
        )}
        <Reveal delay={150}>
          <DecorCalculator d={d} slug={slug} />
        </Reveal>
      </div>
    </section>
  );
}

function TeamSection({ d, enquiry, slug }) {
  const members = (d.members || []).filter((m) => m && (m.name || m.photo));
  if (!members.length && !d.heading && !d.overline) return null;
  return (
    <section className={`bg-bone/50 ${sectionPad(d.spacing)}`} data-testid="section-team">
      <div className="container-x">
        {(d.overline || d.heading || d.intro) && (
          <div className={`max-w-3xl mb-10 md:mb-14 ${alignCls(d.align)} ${alignMx(d.align)}`}>
            {d.overline && <Reveal><div className="overline accent-color">{d.overline}</div></Reveal>}
            {d.heading && <Reveal delay={100}><h2 className="display-2 mt-5">{d.heading}</h2></Reveal>}
            {d.intro && <Reveal delay={200}><p className="body-lead mt-6 text-ink/70">{d.intro}</p></Reveal>}
          </div>
        )}
        <Reveal delay={150}>
          <div className="relative">
            <div className="flex gap-6 md:gap-10 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-4 -mx-6 px-6 md:mx-0 md:px-0" data-testid="team-strip">
              {members.map((m, i) => (
                <div
                  key={i}
                  className={`group flex-shrink-0 w-[168px] md:w-[196px] snap-start ${i % 2 === 1 ? "md:mt-10" : ""}`}
                  data-testid={`team-member-${i}`}
                >
                  <div className="relative aspect-[3/4] rounded-t-full overflow-hidden bg-ink/5 border border-ink/10 transition-all duration-500 group-hover:border-[var(--prop-accent,#9E7B4F)] group-hover:shadow-[0_24px_50px_-24px_rgba(0,0,0,0.35)]">
                    {m.photo ? (
                      <SmartMedia media={m.photo} alt={m.name || "Team member"} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                    ) : (
                      <div className="absolute inset-0 flex items-end justify-center pb-6">
                        <span className="font-display text-5xl text-ink/15">{(m.name || "?").charAt(0)}</span>
                      </div>
                    )}
                  </div>
                  <div className="pt-4 text-center px-1">
                    {m.name && <div className="font-display text-lg leading-tight">{m.name}</div>}
                    {m.role && <div className="text-[10px] uppercase tracking-[0.2em] accent-color mt-1.5">{m.role}</div>}
                    {m.description && <p className="text-xs text-graphite leading-relaxed mt-2.5">{m.description}</p>}
                  </div>
                </div>
              ))}
            </div>
            {members.length > 4 && (
              <div className="hidden md:block absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-bone/90 to-transparent pointer-events-none" />
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function AlbumSection({ d, enquiry, slug }) {
  return (
    <section className={`bg-ink text-ivory ${sectionPad(d.spacing)}`} data-testid="section-album">
      <div className="container-x">
        <div className={alignCls(d.align)}>
          {d.overline && <Reveal><div className="overline accent-color">{d.overline}</div></Reveal>}
          {d.heading && <Reveal delay={100}><h2 className="display-2 mt-6 text-ivory">{d.heading}</h2></Reveal>}
          {d.intro && <Reveal delay={150}><p className={`body-lead mt-6 text-ivory/70 max-w-2xl ${alignMx(d.align)}`}>{d.intro}</p></Reveal>}
        </div>
        <Reveal delay={200}>
          <div className="mt-10 md:mt-14">
            <AlbumBook title={d.heading || ""} slug={slug} categories={d.categories || []} enhance={!!d.enhance} endHeading={d.end_heading} endMessage={d.end_message} endFooter={d.end_footer} />
          </div>
        </Reveal>
        <SectionCTA cta={d.cta} enquiry={enquiry} slug={slug} dark />
      </div>
    </section>
  );
}

export function SectionCTA({ cta, enquiry, slug, dark = false }) {
  if (!cta || !cta.enabled || !cta.label) return null;
  const onClick = (e) => {
    if (cta.action === "call" && cta.phone) {
      window.location.href = `tel:${String(cta.phone).replace(/[^\d+]/g, "")}`;
      return;
    }
    if (cta.action === "link" && cta.url) {
      const url = cta.url;
      if (url.startsWith("/") || url.startsWith("#") || url.startsWith("mailto:") || url.startsWith("tel:")) {
        window.location.href = url;
      } else {
        e.preventDefault();
        window.open(url, "_blank", "noopener,noreferrer");
      }
      return;
    }
    enquiry?.open?.(slug);
  };
  return (
    <div className={`container-x -mt-2 md:-mt-4 pb-14 md:pb-20 text-center ${dark ? "bg-ink" : ""}`} data-testid="section-cta">
      <button
        type="button"
        onClick={onClick}
        data-testid="section-cta-button"
        className={`inline-flex items-center px-10 py-4 text-xs uppercase tracking-[0.22em] font-medium transition-colors ${
          dark ? "bg-ivory text-ink hover:bg-[var(--prop-accent)] hover:text-white" : "bg-ink text-ivory hover:bg-[var(--prop-accent)]"
        }`}
      >
        {cta.label}
      </button>
    </div>
  );
}

export function CustomSection({ sec, enquiry, slug }) {
  const R = RENDERERS[sec.type];
  if (!R) return null;
  const dark = !!(sec.data && (sec.data.dark || sec.data.style === "dark"));
  return (
    <div data-testid={`custom-section-${sec.type}`}>
      <R d={sec.data || {}} enquiry={enquiry} slug={slug} />
      <SectionCTA cta={sec.data?.cta} enquiry={enquiry} slug={slug} dark={dark} />
    </div>
  );
}
